import FormError from '@Components/FormError/FormError';
import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AssetStatusServices from '@Services/AssetStatus.services.ts';
import AssetTemplateServices from '@Services/AssetTemplate.services';
import AssetTypeServices from '@Services/AssetType.services.ts';
import LocationServices from '@Services/Location.services.ts';
import TaxStatusServices from '@Services/TaxStatus.services.ts';
import { Asset, AssetDocument, AssetStatus, AssetTemplate, AssetType, Location, TaxStatus } from '@Types/Asset.types';
import { AssetFormFields, MultiDropOption } from '@Types/Form.types';
import { ASSET_MODAL_TYPE, DROPDOWN_DEFAULT, FILE_VALIDATION } from '@Utils/constants';
import {
  formatRequired,
  getDocumentName,
  isAssetDescriptionRequired,
  isSerialNumberRequired,
  removeRequired,
} from '@Utils/form.utils';
import {
  formatAssetStatusDropdown,
  formatAssetTemplateDropdown,
  formatAssetTypeDropdown,
  formatLocationDropdown,
  formatTaxStatusDropdown,
} from '@Utils/format.utils.tsx';
import { clearInputs } from '@Utils/input.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import {
  Checkbox,
  DatePickerV2,
  Dropdown,
  ErrorDisplay,
  FileUpload,
  FreeText,
  LogoLoader,
  MultiSelectDropdown,
  TextArea,
  Toast,
} from '@kg-internal/pv3-uikit';
import { AxiosResponse } from 'axios';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import './AssetInputModal.scss';

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  asset?: Asset;
  assetTemplate?: AssetTemplate;
  onSubmit: (formData: FormData, id?: string) => Promise<AxiosResponse>;
  formatter: (fields: AssetFormFields) => FormData;
  title: string;
  type: ASSET_MODAL_TYPE;
};

const { LEGAL_INFO_RULES, DEVICE_INFO_RULES, LOCATION_INFO_RULES } = VALIDATION_RULES;

const AssetInputModal = ({
  show,
  onClose,
  onSuccess,
  asset,
  assetTemplate,
  onSubmit,
  formatter,
  title,
  type,
}: Props) => {
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [assetStatuses, setAssetStatuses] = useState<AssetStatus[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [taxStatuses, setTaxStatuses] = useState<TaxStatus[]>([]);
  const [assetTemplates, setAssetTemplates] = useState<AssetTemplate[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [typeNumber, setTypeNumber] = useState<string | undefined>(LEGAL_INFO_RULES.ASSET_NUMBER.placeholder);
  const [dateSelected, setDateSelected] = useState<boolean>(false);
  const [fileNames, setFileNames] = useState<MultiDropOption[]>([]);
  const [filename, setFilename] = useState<string>('');
  const [deletedDocuments, setDeletedDocuments] = useState<AssetDocument[]>([]);
  const [isSerialRequired, setIsSerialRequired] = useState(false);
  const [isDescriptionRequired, setIsDescriptionRequired] = useState(false);
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
    register,
    setValue,
    getValues,
    unregister,
  } = useForm<AssetFormFields>({
    defaultValues: {
      assetTemplate: DROPDOWN_DEFAULT.value,
      name: '',
      assetMake: '',
      assetModel: '',
      assetYear: '',
      assetType: DROPDOWN_DEFAULT.value,
      assetStatus: DROPDOWN_DEFAULT.value,
      osVersion: '',
      storage: '',
      sharedDevice: false,
      descriptionDetails: '',
      officeId: DROPDOWN_DEFAULT.value,
      locationDetail: '',
      inOffice: false,
      serialNumber: '',
      assetTag: '',
      rfid: '',
      taxStatus: DROPDOWN_DEFAULT.value,
      vendor: '',
      purchaseDate: !dateSelected ? undefined : new Date(),
      warrantyExpiry: undefined,
      invoiceNumber: '',
      insideRegime: false,
      documents: [],
    },
    mode: 'onChange',
  });
  const dateWatch = useWatch({
    control,
    name: 'purchaseDate',
    defaultValue: !dateSelected ? undefined : new Date(),
  });
  const assetTemplateWatch = watch('assetTemplate');

  const onDateChange = (value: Date) => {
    setValue('purchaseDate', value);
  };

  const assetTypeWatch = watch('assetType');

  const handleClose = (e?: Event) => {
    e?.preventDefault();
    onClose();
    reset();
    setFileNames([]);

    // Some inputs from UI Kit do not get reset automatically, need to clear manually
    clearInputs('text');
    clearInputs('number');
    clearInputs('hidden');
  };

  const submitHandler = (data: AssetFormFields) => {
    data.deletedDocuments = deletedDocuments;
    const formattedData = formatter(data);
    const id: string | undefined = asset ? asset.id : undefined;
    onSubmit(formattedData, id)
      .then(() => {
        onSuccess();
        handleClose();
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage, true);
      });
  };

  const handleFileUpload = (files: File[]) => {
    files.forEach((file, index) => {
      if (file.size > FILE_VALIDATION.MAX_SIZE) {
        const error = `File ${file.name} exceeds the maximum allowed size.`;
        setFilename(error);
        Toast.warning(error);
        files.splice(index, 1);
      }
    });
    const currentFiles = getValues('documents');

    const newFiles = currentFiles.concat(files);

    setValue('documents', newFiles);
    setFileNames(
      newFiles.map((doc) => ({
        value: getDocumentName(doc),
        label: getDocumentName(doc),
      })),
    );
  };

  const handleFileDelete = (value: MultiDropOption) => {
    const currentFiles = getValues('documents');
    const deletedFile: File | AssetDocument | undefined = currentFiles.find(
      (doc) => value.value === getDocumentName(doc),
    );
    // When an existing file is deleted
    if (deletedFile && !(deletedFile instanceof File)) {
      setDeletedDocuments((prev) => [...prev, deletedFile]);
    }
    const newFiles = currentFiles.filter((doc) => value.value !== getDocumentName(doc));
    setValue('documents', newFiles);
    setFileNames(
      newFiles.map((doc) => ({
        value: getDocumentName(doc),
        label: getDocumentName(doc),
      })),
    );
  };

  useEffect(() => {
    Promise.all([
      AssetStatusServices.getAssetStatus(null, false),
      AssetTypeServices.getAssetTypes(null, false),
      LocationServices.getLocations(null, false),
      TaxStatusServices.getTaxStatus(null, false),
      AssetTemplateServices.getAssetTemplate(null, false),
    ])
      .then(([statusData, typeData, locationData, taxStatusData, assetTemplateData]) => {
        setAssetTypes(typeData.data.docs);
        setAssetStatuses(statusData.data.docs);
        setLocations(locationData.data.docs);
        setTaxStatuses(taxStatusData.data.docs);
        setAssetTemplates(assetTemplateData.data.docs);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!assetTypeWatch) {
      return;
    }

    const foundType = assetTypes.find((assetType) => assetType.id === assetTypeWatch);

    setTypeNumber(foundType?.assetCode);

    // Need to unregister previous value to remove the 'required' value
    unregister('serialNumber');
    unregister('rfid');
    unregister('descriptionDetails');
    unregister('warrantyExpiry');
  }, [assetTypeWatch, assetTypes, unregister]);

  const setDefaultValues = (data: Asset | AssetTemplate | undefined) => {
    if (!data) {
      return;
    }
    if (assetTemplate) {
      setValue('name', (data as AssetTemplate)?.name);
    }

    if (asset) {
      setValue('serialNumber', (data as Asset)?.serialNumber);
      setValue('rfid', (data as Asset)?.rfid);
      setFileNames(
        (data as Asset)?.documents.map((doc) => ({
          value: doc.filename,
          label: doc.filename,
        })),
      );
      setValue('documents', (data as Asset).documents);
    }

    setValue('assetMake', data?.assetMake);
    setValue('assetModel', data?.assetModel);
    setValue('assetYear', data?.assetYear);
    setValue('assetType', assetTypes.find((assetType) => assetType.description === data?.assetType)?.id || '');
    setValue('assetStatus', assetStatuses.find((status) => status.description === data?.assetStatus)?.id || '');
    setValue('osVersion', data?.osVersion);
    setValue('storage', data?.storage);
    setValue('sharedDevice', data?.sharedDevice);
    setValue('descriptionDetails', data?.descriptionDetails);
    setValue('officeId', locations.find((location) => location.name === data?.officeName)?.id || '');
    setValue('locationDetail', data?.locationDetail);
    setValue('inOffice', data?.inOffice);
    setValue('assetTag', data.assetTag);
    setValue('taxStatus', taxStatuses.find((tax) => tax.description === data?.taxStatus)?.id || '');
    setValue('vendor', data?.vendor);
    if (data?.purchaseDate) {
      setValue('purchaseDate', new Date(data?.purchaseDate));
      setDateSelected(true);
    }
    if (data?.warrantyExpiry) {
      setValue('warrantyExpiry', new Date(data?.warrantyExpiry));
    }
    setValue('invoiceNumber', data?.invoiceNumber);
    setValue('insideRegime', data?.insideRegime);
    const foundType = assetTypes.find((assetType) => assetType.description === data?.assetType);
    setTypeNumber(foundType?.assetCode);
  };

  useEffect(() => {
    setTypeNumber(LEGAL_INFO_RULES.ASSET_NUMBER.placeholder);
    setDefaultValues(asset || assetTemplate);
  }, [asset, assetTemplate, show]);

  useEffect(() => {
    const selectedAssetTemplate = assetTemplates.find((template) => template.id === assetTemplateWatch);
    if (!selectedAssetTemplate) {
      reset();
    }
    setDefaultValues(selectedAssetTemplate);
  }, [assetTemplateWatch]);

  useEffect(() => {
    if (assetTypeWatch) {
      isSerialNumberRequired(assetTypeWatch).then(setIsSerialRequired);
      isAssetDescriptionRequired(assetTypeWatch).then(setIsDescriptionRequired);
    } else {
      setIsSerialRequired(false);
    }
  }, [assetTypeWatch]);

  if (loading) {
    return (
      <ModalWrapper title={title} show={show} handleClose={handleClose} disablePrimary>
        <LogoLoader inline />
      </ModalWrapper>
    );
  }

  // Can't have a <form> tag due to file upload button also triggering submit when not intended to.
  return (
    <ModalWrapper
      title={title}
      show={show}
      primaryAction={() => handleSubmit(submitHandler)()}
      handleClose={handleClose}>
      {errorMessage ? (
        <ErrorDisplay inline details={errorMessage} />
      ) : (
        <div className='grid'>
          <div className='col-12'>
            {type === ASSET_MODAL_TYPE.ASSET_TEMPLATE && (
              <FreeText
                label={formatRequired('Template Name')}
                error={!!errors.name}
                errorMessage={errors.name?.message}
                {...register('name', VALIDATION_RULES.TEMPLATE_NAME)}
              />
            )}

            {type === ASSET_MODAL_TYPE.ASSET && !asset && (
              <Dropdown.RHF
                options={formatAssetTemplateDropdown(assetTemplates)}
                label='Asset Template'
                name='assetTemplate'
                control={control}
                id='assetTemplate'
              />
            )}
          </div>
          <div className='col-12 col-md-4'>
            <h2>Device Info</h2>
            <FreeText
              label={type === ASSET_MODAL_TYPE.ASSET ? formatRequired('Device Brand') : 'Device Brand'}
              fullWidth
              error={!!errors.assetMake}
              errorMessage={errors.assetMake?.message}
              {...register(
                'assetMake',
                type === ASSET_MODAL_TYPE.ASSET_TEMPLATE
                  ? removeRequired(DEVICE_INFO_RULES.MAKE)
                  : DEVICE_INFO_RULES.MAKE,
              )}
            />
            <FreeText
              label='Model'
              fullWidth
              error={!!errors.assetModel}
              errorMessage={errors.assetModel?.message}
              {...register('assetModel', DEVICE_INFO_RULES.MODEL)}
            />
            <FreeText
              label='Year'
              fullWidth
              error={!!errors.assetYear}
              errorMessage={errors.assetYear?.message}
              {...register('assetYear', DEVICE_INFO_RULES.YEAR)}
            />
            <span
              className={cx({
                'form-error': errors.assetType?.message,
              })}>
              <Dropdown.RHF
                options={formatAssetTypeDropdown(assetTypes)}
                label={type === ASSET_MODAL_TYPE.ASSET ? formatRequired('Asset Type') : 'Asset Type'}
                fullWidth
                name='assetType'
                control={control}
                rules={
                  type === ASSET_MODAL_TYPE.ASSET
                    ? DEVICE_INFO_RULES.ASSET_TYPE
                    : removeRequired(DEVICE_INFO_RULES.ASSET_TYPE)
                }
                id='assetType'
              />
              <FormError error={errors.assetType?.message} />
            </span>
            <span
              className={cx({
                'form-error': errors.assetStatus?.message,
              })}>
              <Dropdown.RHF
                options={formatAssetStatusDropdown(assetStatuses)}
                label={type === ASSET_MODAL_TYPE.ASSET ? formatRequired('Status') : 'Status'}
                fullWidth
                name='assetStatus'
                control={control}
                rules={
                  type === ASSET_MODAL_TYPE.ASSET
                    ? DEVICE_INFO_RULES.ASSET_STATUS
                    : removeRequired(DEVICE_INFO_RULES.ASSET_STATUS)
                }
              />
              <FormError error={errors.assetStatus?.message} />
            </span>

            <FreeText
              label='Chip'
              fullWidth
              error={!!errors.osVersion}
              errorMessage={errors.osVersion?.message}
              {...register('osVersion', DEVICE_INFO_RULES.OS_VERSION)}
            />
            <FreeText
              label='Storage'
              fullWidth
              error={!!errors.storage}
              errorMessage={errors.storage?.message}
              {...register('storage', DEVICE_INFO_RULES.STORAGE)}
            />
            <Checkbox label='Shared Device' {...register('sharedDevice')}>
              Shared Device
            </Checkbox>
            <br />
            <span
              className={cx({
                'form-error': errors.descriptionDetails?.message,
              })}>
              <TextArea
                label={isDescriptionRequired ? formatRequired('Asset Description') : 'Asset Description'}
                {...register('descriptionDetails', DEVICE_INFO_RULES.DESCRIPTION)}
              />
              <FormError error={errors.descriptionDetails?.message} />
            </span>
          </div>
          <div className='col-12 col-md-8'>
            <h2>Location Info</h2>
            <div className='grid'>
              <div className='col-12 col-md-6'>
                <span
                  className={cx({
                    'form-error': errors.officeId?.message,
                  })}>
                  <Dropdown.RHF
                    options={formatLocationDropdown(locations)}
                    label={type === ASSET_MODAL_TYPE.ASSET ? formatRequired('Office') : 'Office'}
                    fullWidth
                    name='officeId'
                    control={control}
                    rules={
                      type === ASSET_MODAL_TYPE.ASSET
                        ? LOCATION_INFO_RULES.OFFICE
                        : removeRequired(LOCATION_INFO_RULES.OFFICE)
                    }
                  />
                  <FormError error={errors.officeId?.message} />
                </span>
                <Checkbox label='In Location' {...register('inOffice')}>
                  In Location
                </Checkbox>
              </div>
              <div className='col-12 col-md-6'>
                <span
                  className={cx({
                    'form-error': errors.locationDetail?.message,
                  })}>
                  <TextArea label='Location Details' {...register('locationDetail', LOCATION_INFO_RULES.DETAILS)} />
                  <FormError error={errors.locationDetail?.message} />
                </span>
              </div>
            </div>
            <h2>Legal Info</h2>
            <div className='grid'>
              <div className='col-12 col-md-4'>
                {type === ASSET_MODAL_TYPE.ASSET && (
                  <FreeText
                    label={isSerialRequired ? formatRequired('Serial Number') : 'Serial Number'}
                    fullWidth
                    error={!!errors.serialNumber}
                    errorMessage={errors.serialNumber?.message}
                    {...register('serialNumber', LEGAL_INFO_RULES.SERIAL_NUMBER_ALT)}
                  />
                )}
                <span
                  className={cx({
                    'form-error': errors.taxStatus?.message,
                  })}>
                  <Dropdown.RHF
                    options={formatTaxStatusDropdown(taxStatuses)}
                    label={type === ASSET_MODAL_TYPE.ASSET ? formatRequired('Tax Status') : 'Tax Status'}
                    fullWidth
                    name='taxStatus'
                    control={control}
                    rules={
                      type === ASSET_MODAL_TYPE.ASSET
                        ? LEGAL_INFO_RULES.TAX_STATUS
                        : removeRequired(LEGAL_INFO_RULES.TAX_STATUS)
                    }
                  />
                  <FormError error={errors.taxStatus?.message} />
                </span>
                <DatePickerV2
                  label='Warranty Expiry'
                  onChange={(value: Date) => setValue('warrantyExpiry', value)}
                  id='warranty-expiry-picker'
                  fullWidth
                  value={watch('warrantyExpiry')}
                />
              </div>
              <div className='col-12 col-md-4'>
                <FreeText
                  label='Asset Number'
                  fullWidth
                  error={!!errors.assetTag}
                  errorMessage={errors.assetTag?.message}
                  placeholder={typeNumber}
                  {...register('assetTag', LEGAL_INFO_RULES.ASSET_NUMBER)}
                />
                <FreeText
                  label='Vendor'
                  fullWidth
                  error={!!errors.vendor}
                  errorMessage={errors.vendor?.message}
                  {...register('vendor', LEGAL_INFO_RULES.VENDOR)}
                />
                <FreeText
                  label='Invoice Number'
                  fullWidth
                  error={!!errors.invoiceNumber}
                  errorMessage={errors.invoiceNumber?.message}
                  {...register('invoiceNumber', LEGAL_INFO_RULES.INVOICE_NUMBER)}
                />
              </div>
              <div className='col-12 col-md-4'>
                {type === ASSET_MODAL_TYPE.ASSET && (
                  <FreeText
                    label='RFID'
                    fullWidth
                    error={!!errors.rfid}
                    errorMessage={errors.rfid?.message}
                    {...register('rfid', LEGAL_INFO_RULES.RFID)}
                  />
                )}
                <DatePickerV2
                  label='Date of Purchase'
                  onChange={onDateChange}
                  id='date-picker'
                  fullWidth
                  value={dateWatch}
                />
                <br className='hide-mobile' />
                <Checkbox label='Inside Regime' {...register('insideRegime')}>
                  Inside Regime
                </Checkbox>
              </div>
            </div>
            {type === ASSET_MODAL_TYPE.ASSET && (
              <>
                <h2>Documents</h2>
                <span className='document-picker'>
                  {fileNames.length > 0 && (
                    <MultiSelectDropdown options={[]} values={fileNames} fullWidth removeOption={handleFileDelete} />
                  )}
                </span>
                <FileUpload
                  onUpload={handleFileUpload}
                  progress={0}
                  numFiles={getValues('documents').length}
                  link=''
                  fileName={filename}
                  showCopy={false}
                  showDelete={false}
                  error={filename}
                  onDelete={() => {}}
                />
              </>
            )}
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default AssetInputModal;
